import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/Pagination";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Search, Package, Edit, Trash2, Users, Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

const INITIAL_FORM = {
  product_code: "", product_name: "", category: "", group: "",
  list_1: "", list_2: "", material_type: "ROLL", base_rate_per_sqmm: 0,
  rate_per_sheet: null, width: 0, length: null, thickness: null,
  default_wastage_percent: 5, hsn_code: "", gst_percent: 18, lead_time_days: 7, is_active: true,
};

export default function ProductsPage() {
  const { hasRole } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const perPage = 50;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [ratesDialogOpen, setRatesDialogOpen] = useState(false);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesData, setRatesData] = useState(null);
  const [ratesSearch, setRatesSearch] = useState("");
  const canEdit = hasRole("super_admin", "sales_manager");
  const [togglingActive, setTogglingActive] = useState({});
  const [materialTypeFilter, setMaterialTypeFilter] = useState("all"); // all | ROLL | SHEET

  const toggleProductActive = async (product) => {
    setTogglingActive(prev => ({ ...prev, [product.id]: true }));
    try {
      await api.put(`/products/${product.id}`, { is_active: !product.is_active });
      toast.success(`Product ${product.is_active ? "deactivated" : "activated"}`);
      fetchProducts();
    } catch {
      toast.error("Failed to update product status");
    } finally {
      setTogglingActive(prev => ({ ...prev, [product.id]: false }));
    }
  };

  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedProducts = useMemo(() => {
    if (!sortField) return products;
    return [...products].sort((a, b) => {
      let aVal = a[sortField] ?? "";
      let bVal = b[sortField] ?? "";
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      if (typeof aVal === "boolean") {
        return sortDir === "asc" ? (aVal === bVal ? 0 : aVal ? -1 : 1) : (aVal === bVal ? 0 : aVal ? 1 : -1);
      }
      return sortDir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [products, sortField, sortDir]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronsUpDown size={13} className="ml-1 opacity-40 inline-block" />;
    return sortDir === "asc"
      ? <ChevronUp size={13} className="ml-1 inline-block" />
      : <ChevronDown size={13} className="ml-1 inline-block" />;
  };

  const SortHead = ({ field, children, className = "" }) => (
    <TableHead
      className={`bg-slate-900 text-white cursor-pointer select-none hover:bg-slate-700 transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center">
        {children}<SortIcon field={field} />
      </span>
    </TableHead>
  );

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: perPage, active_only: "false" });
      if (search) params.append("search", search);
      if (materialTypeFilter !== "all") params.append("material_type", materialTypeFilter);
      const res = await api.get(`/products?${params}`);
      setProducts(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [page, search, materialTypeFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleOpenDialog = (product) => {
    setEditingProduct(product);
    setForm({ ...product });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/products/${editingProduct.id}`, { ...form, length: form.length || null, thickness: form.thickness || null });
      toast.success("Product updated");
      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await api.delete(`/products/${productId}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch { toast.error("Failed to delete product"); }
  };

  const handleViewRates = async (product) => {
    setRatesData(null);
    setRatesSearch("");
    setRatesDialogOpen(true);
    setRatesLoading(true);
    try {
      const res = await api.get(`/products/${product.id}/customer-rates`);
      setRatesData({ ...res.data, product_name: product.product_name });
    } catch {
      toast.error("Failed to load customer rates");
      setRatesDialogOpen(false);
    } finally {
      setRatesLoading(false);
    }
  };

  const filteredRates = ratesData?.customer_rates?.filter(
    (r) => (r.customer_code || "").toLowerCase().includes(ratesSearch.toLowerCase()) || (r.customer_name || "").toLowerCase().includes(ratesSearch.toLowerCase())
  ) || [];

  return (
    <div className="space-y-6 animate-slide-in" data-testid="products-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase tracking-tight" data-testid="products-title">Product Master</h1>
          <p className="text-slate-500 mt-1">Manage reinforcement components catalog (UOM: Square MM)</p>
        </div>

        {/* Edit Dialog */}
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none">
              <DialogHeader><DialogTitle className="font-heading text-xl uppercase tracking-wider">Edit Product</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="space-y-4">
                  <h3 className="font-heading text-sm uppercase tracking-wider text-slate-500 border-b pb-2">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><Label className="text-xs uppercase tracking-wider">Product Code</Label><Input value={form.product_code} className="rounded-none font-mono bg-slate-100" disabled data-testid="product-code-input" /></div>
                    <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider">Product Name *</Label><Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="rounded-none" required data-testid="product-name-input" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none" data-testid="category-input" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">List 1</Label><Input value={form.list_1} className="rounded-none bg-slate-100" disabled data-testid="list1-input" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">List 2</Label><Input value={form.list_2 || ""} className="rounded-none bg-slate-100" disabled data-testid="list2-input" /></div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="font-heading text-sm uppercase tracking-wider text-slate-500 border-b pb-2">Material & Dimensions (mm)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Material Type</Label>
                      <select value={form.material_type || "ROLL"} onChange={(e) => setForm({ ...form, material_type: e.target.value })} className="flex h-10 w-full border border-input bg-background px-3 py-2 text-sm font-mono rounded-none" data-testid="material-type-select">
                        <option value="ROLL">ROLL</option><option value="SHEET">SHEET</option>
                      </select>
                    </div>
                    <div><Label className="text-xs uppercase tracking-wider">Width (mm) *</Label><Input type="number" step="0.01" value={form.width} onChange={(e) => setForm({ ...form, width: parseFloat(e.target.value) || 0 })} className="rounded-none font-mono" required data-testid="width-input" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Length (mm)</Label><Input type="number" step="0.01" value={form.length || ""} onChange={(e) => setForm({ ...form, length: parseFloat(e.target.value) || null })} className="rounded-none font-mono" data-testid="length-input" /></div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Thickness (mm)</Label>
                      <Input type="number" step="0.01" min="0" value={form.thickness || ""} onChange={(e) => setForm({ ...form, thickness: parseFloat(e.target.value) || null })} className="rounded-none font-mono" placeholder="e.g. 1.5" data-testid="thickness-input" />
                      <p className="text-xs text-slate-400 mt-1">Used for layer count calculation</p>
                    </div>
                    <div><Label className="text-xs uppercase tracking-wider">Wastage %</Label><Input type="number" step="0.1" value={form.default_wastage_percent} onChange={(e) => setForm({ ...form, default_wastage_percent: parseFloat(e.target.value) || 0 })} className="rounded-none font-mono" data-testid="wastage-input" /></div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="bg-slate-900 text-white rounded-none btn-industrial" data-testid="save-product-button">{submitting ? "Saving..." : "Update Product"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Customer Rates Dialog */}
        <Dialog open={ratesDialogOpen} onOpenChange={setRatesDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-none">
            <DialogHeader><DialogTitle className="font-heading text-lg uppercase tracking-wider">Customer Special Prices</DialogTitle></DialogHeader>
            {ratesLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : ratesData ? (
              <div className="space-y-4 mt-2">
                <div className="bg-slate-50 border border-slate-200 p-3">
                  <p className="font-medium text-sm">{ratesData.product_name}</p>
                  <p className="font-mono text-xs text-slate-500">{ratesData.product_code}</p>
                  <p className="text-sm mt-1">Standard (ALL) Rate: <span className="font-mono font-semibold">{ratesData.all_rate > 0 ? `₹${ratesData.all_rate.toLocaleString()}` : "Not set"}</span></p>
                </div>
                {ratesData.customer_rates.length === 0 ? (
                  <div className="text-center py-8 text-slate-400"><Users className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No customer-specific rates found</p></div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 uppercase tracking-wider">{ratesData.customer_rates.length} special price(s)</span>
                      <div className="relative w-56"><Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" size={14} /><Input placeholder="Search customer..." value={ratesSearch} onChange={(e) => setRatesSearch(e.target.value)} className="pl-8 h-8 rounded-none text-sm" data-testid="rates-search-input" /></div>
                    </div>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="bg-slate-900 text-white text-xs">Customer Code</TableHead>
                        <TableHead className="bg-slate-900 text-white text-xs">Customer Name</TableHead>
                        <TableHead className="bg-slate-900 text-white text-xs text-right">Special Rate</TableHead>
                        <TableHead className="bg-slate-900 text-white text-xs text-right">vs Standard</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {filteredRates.slice(0, 100).map((r, i) => {
                          const diff = ratesData.all_rate > 0 ? ((r.rate - ratesData.all_rate) / ratesData.all_rate * 100).toFixed(1) : null;
                          return (
                            <TableRow key={i}>
                              <TableCell className="font-mono text-xs">{r.customer_code}</TableCell>
                              <TableCell className="text-sm">{r.customer_name || "-"}</TableCell>
                              <TableCell className="font-mono text-sm text-right font-medium">₹{r.rate?.toLocaleString()}</TableCell>
                              <TableCell className="text-right">{diff !== null && <span className={`font-mono text-xs ${parseFloat(diff) > 0 ? "text-red-600" : parseFloat(diff) < 0 ? "text-green-600" : "text-slate-400"}`}>{parseFloat(diff) > 0 ? "+" : ""}{diff}%</span>}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {filteredRates.length > 100 && <p className="text-xs text-slate-400 text-center">Showing first 100 of {filteredRates.length}</p>}
                  </>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input placeholder="Search by name, code, category, group, list..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-9 rounded-none" data-testid="search-input" />
        </div>
        <div className="flex gap-1">
          {[["all","All"],["ROLL","Roll"],["SHEET","Sheet"]].map(([v,l]) => (
            <button key={v} onClick={() => { setMaterialTypeFilter(v); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-none border transition-colors ${materialTypeFilter === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <Card className="card-industrial overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <SortHead field="product_code">Code</SortHead>
              <SortHead field="product_name">Product Name</SortHead>
              <SortHead field="list_1">List 1</SortHead>
              <SortHead field="list_2">List 2</SortHead>
              <SortHead field="material_type">Type</SortHead>
              <SortHead field="width">Width (mm)</SortHead>
              <SortHead field="thickness">Thickness (mm)</SortHead>
              <SortHead field="erp_rate">ERP Rate</SortHead>
              <SortHead field="is_active">Status</SortHead>
              {canEdit && <TableHead className="bg-slate-900 text-white">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={canEdit ? 10 : 9} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></TableCell></TableRow>
            ) : products.length === 0 ? (
              <TableRow><TableCell colSpan={canEdit ? 10 : 9} className="text-center py-12 text-slate-500"><Package className="w-12 h-12 mx-auto mb-3 opacity-20" /><p>No products found</p></TableCell></TableRow>
            ) : (
              sortedProducts.map((product) => (
                <TableRow key={product.id} className="hover:bg-slate-50" data-testid={`product-row-${product.id}`}>
                  <TableCell className="font-mono text-sm">{product.product_code}</TableCell>
                  <TableCell className="font-medium">{product.product_name}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-600">{product.list_1 || "-"}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-600">{product.list_2 || "-"}</TableCell>
                  <TableCell><Badge className={product.material_type === "SHEET" ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-amber-100 text-amber-800 border-amber-200"}>{product.material_type || "ROLL"}</Badge></TableCell>
                  <TableCell className="font-mono">{product.width}{product.material_type === "SHEET" && product.length ? ` x ${product.length}` : ""}</TableCell>
                  <TableCell className="font-mono">{product.thickness != null ? product.thickness : <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="font-mono">{product.erp_rate > 0 ? `₹${product.erp_rate.toLocaleString()}` : <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={product.is_active}
                        onCheckedChange={() => canEdit && toggleProductActive(product)}
                        disabled={!canEdit || togglingActive[product.id]}
                        data-testid={`active-toggle-${product.id}`}
                      />
                      <Badge className={product.is_active ? "bg-green-100 text-green-800 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                        {product.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleViewRates(product)} className="h-8 w-8 p-0 text-violet-600 hover:text-violet-700 hover:bg-violet-50" title="View customer special prices" data-testid={`view-rates-${product.id}`}><Users size={16} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleOpenDialog(product)} className="h-8 w-8 p-0" data-testid={`edit-product-${product.id}`}><Edit size={16} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(product.id)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-product-${product.id}`}><Trash2 size={16} /></Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div className="p-4">
          <Pagination page={page} totalPages={totalPages} total={total} perPage={perPage} onPageChange={setPage} />
        </div>
      </Card>
    </div>
  );
}
